import { useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import {
  GetWasteContainerQuery,
  ReconcileChoice,
  UnitType,
  WasteContainerStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { ReactNode, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { GET_WASTE_CONTAINER_QUERY } from "./api";
import { ConsolidateContainersDialog } from "./ConsolidateContainersDialog";
import {
  containerTypeLabel,
  NO_VALUE_PLACEHOLDER,
  formatContainerDate,
  formatDaysRemaining,
  formatQuantity,
  formatReportablePounds,
  reportablePounds,
  STATUS_TRANSITION_OPTIONS,
} from "./containerDisplay";
import { ContainerFillCard } from "./ContainerFillCard";
import { ContainerFillEventDialog } from "./ContainerFillEventDialog";
import { ContainerHistorySection } from "./ContainerHistorySection";
import { EmptyContainerDialog } from "./EmptyContainerDialog";
import { FillHistorySection } from "./FillHistorySection";
import { MoveContainerDialog } from "./MoveContainerDialog";
import { SetContainerStatusDialog } from "./SetContainerStatusDialog";
import { WasteContainerClockChip } from "./WasteContainerClockChip";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type AccumulationLimitRow = WasteContainerDetailData["clock"]["limits"][number];

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function FieldValue({
  label,
  children,
  secondary,
}: {
  label: string;
  children: ReactNode;
  /** Extra content below the main value — e.g. the reportable-pounds hint under an amount. */
  secondary?: ReactNode;
}) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
      {secondary}
    </Grid>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/** Muted empty-state box for a section with nothing to show. */
function EmptySectionState({ message }: { message: string }) {
  return (
    <Box
      sx={{
        py: 4,
        textAlign: "center",
        backgroundColor: "grey.50",
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

/**
 * "≈ 1,500 lb" under a mass amount; a muted "specific gravity needed" hint
 * under a volume amount (toReportable can't convert volume to weight without
 * a density, and specificGravity is always null until Project 2 lands stream
 * density). Renders nothing when there's no amount to annotate.
 */
function ReportableAmountHint({
  amount,
  unit,
}: {
  amount: number | null | undefined;
  unit: UnitType | null | undefined;
}) {
  if (amount == null || !unit) {
    return null;
  }
  const lb = reportablePounds(amount, unit);
  if (lb !== null) {
    return (
      <Typography variant="caption" color="text.secondary" display="block">
        {formatReportablePounds(lb)}
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        Specific gravity needed to compute pounds
      </Typography>
      <InfoIconTooltip tooltip="This amount is recorded by volume. Once the waste stream has a specific gravity, this container's amount will also show in pounds." />
    </Stack>
  );
}

/**
 * True when the container is actively running a clock (placed, not
 * archived, not shipped) but the facility's generator status couldn't be
 * resolved — the resolver reports this directly via
 * `clock.generatorStatusResolved` rather than being inferred from an empty
 * limits list, since a resolved VSQG facility legitimately produces zero
 * limits too (VSQG central has no federal TIME rule; VSQG satellite is
 * exempt from the SAA volume limit).
 */
function needsGeneratorStatusCallout(container: WasteContainerDetailData): boolean {
  return (
    container.accumulationArea != null &&
    !container.archivedAt &&
    container.status !== WasteContainerStatus.Shipped &&
    !container.clock.generatorStatusResolved
  );
}

function AccumulationLimitRows({ limits }: { limits: AccumulationLimitRow[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Basis</TableCell>
          <TableCell>Deadline</TableCell>
          <TableCell>Days remaining</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {limits.map((limit, index) => (
          <TableRow key={`${limit.limitType}-${index}`}>
            <TableCell>{limit.basis}</TableCell>
            <TableCell>{formatContainerDate(limit.deadlineDate)}</TableCell>
            <TableCell>{formatDaysRemaining(limit.daysRemaining)}</TableCell>
            <TableCell>
              <WasteContainerClockChip status={limit.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AccumulationClockSection({ container }: { container: WasteContainerDetailData }) {
  const { clock } = container;

  let body: ReactNode;
  if (clock.limits.length > 0) {
    body = <AccumulationLimitRows limits={clock.limits} />;
  } else if (needsGeneratorStatusCallout(container)) {
    // Explanatory, with no call to action on purpose: generator status is
    // derived — RCRAInfo's reported status for the facility's EPA id, or the
    // status calculated from its shipment history — so there is no field for
    // the reader to go set. Telling them to set it would send them to a
    // read-only indicator on the facility page.
    body = (
      <Alert severity="info">
        Storage deadlines need this facility&apos;s generator status, which isn&apos;t known yet.
        Generator status comes from the facility&apos;s EPA registration or its shipment history.
      </Alert>
    );
  } else {
    body = <EmptySectionState message="No accumulation clock applies to this container." />;
  }

  return <SectionCard title="Accumulation clock and limits">{body}</SectionCard>;
}

function ContainerDetailContent({ container }: { container: WasteContainerDetailData }) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [emptyDialogOpen, setEmptyDialogOpen] = useState(false);
  const [consolidateDialogOpen, setConsolidateDialogOpen] = useState(false);
  const [reconcileFillDialogOpen, setReconcileFillDialogOpen] = useState(false);
  const { tenantId } = useTenant();
  const isArchived = !!container.archivedAt;
  const hasStatusTransitions = STATUS_TRANSITION_OPTIONS[container.status].length > 0;

  return (
    <Stack spacing={3} sx={{ px: 3, py: 2 }}>
      {/* space-between alone leaves no minimum gap, so the status chips sit
          flush against the first action button before the row is narrow enough
          to wrap. The explicit spacing guarantees the gap and the wrap gives the
          actions their own line instead of compressing them. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
          <Typography variant="h5">{container.containerLabel}</Typography>
          <WasteContainerStatusChip status={container.status} />
          {isArchived && <Chip label="Archived" size="small" variant="outlined" />}
        </Stack>
        {!isArchived && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHoriz />}
              onClick={() => setMoveDialogOpen(true)}
            >
              Move
            </Button>
            {container.isPermanent && (
              <Button variant="outlined" size="small" onClick={() => setEmptyDialogOpen(true)}>
                Empty tank
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={() => setConsolidateDialogOpen(true)}>
              Consolidate
            </Button>
            {hasStatusTransitions && (
              <Button variant="outlined" size="small" onClick={() => setStatusDialogOpen(true)}>
                Change status
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit
            </Button>
          </Stack>
        )}
      </Stack>

      {isArchived && (
        <Alert severity="info" variant="outlined">
          This container was archived on {formatContainerDate(container.archivedAt)} and is
          read-only.
          {container.shippedOnShipment && (
            <>
              {" It left the site on "}
              <Link
                component={RouterLink}
                to={`/o/${tenantId}/waste/shipments/${container.shippedOnShipment.id}`}
              >
                {container.shippedOnShipment.manifestRequestNumber}
              </Link>
              .
            </>
          )}
        </Alert>
      )}

      <SectionCard title="Details">
        <Grid container spacing={2}>
          <FieldValue label="Facility">
            {container.facility?.name ? (
              <Link
                component={RouterLink}
                to={`/o/${tenantId}/waste/facilities/${container.facilityId}`}
              >
                {container.facility.name}
              </Link>
            ) : (
              NO_VALUE_PLACEHOLDER
            )}
          </FieldValue>
          <FieldValue label="Container type">
            {containerTypeLabel(container.containerType)}
          </FieldValue>
          <FieldValue label="Capacity">
            {formatQuantity(container.capacity, container.capacityUnit)}
          </FieldValue>
          <FieldValue
            label="Amount on hand"
            secondary={
              <ReportableAmountHint amount={container.amount} unit={container.amountUnit} />
            }
          >
            {formatQuantity(container.amount, container.amountUnit)}
            {container.isEstimate && container.amount != null ? " (estimate)" : ""}
          </FieldValue>
          <FieldValue label="Waste stream">
            {container.wasteStream?.name ?? NO_VALUE_PLACEHOLDER}
          </FieldValue>
          <FieldValue label="Accumulation area">
            {container.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER}
          </FieldValue>
          <FieldValue label="Accumulation start date">
            {formatContainerDate(container.accumulationStartDate)}
          </FieldValue>
          <FieldValue label="Moved to central date">
            {formatContainerDate(container.movedToCentralDate)}
          </FieldValue>
          <FieldValue label="Shipped on">
            {container.shippedOnShipment ? (
              <Link
                component={RouterLink}
                to={`/o/${tenantId}/waste/shipments/${container.shippedOnShipment.id}`}
              >
                {container.shippedOnShipment.manifestRequestNumber}
              </Link>
            ) : (
              NO_VALUE_PLACEHOLDER
            )}
          </FieldValue>
          <FieldValue label="Estimated pickup date">
            {formatContainerDate(container.estimatedPickupDate)}
          </FieldValue>
          <FieldValue label="Permanent vessel (tank)">{yesNo(container.isPermanent)}</FieldValue>
          <FieldValue label="Flow-through">{yesNo(container.isFlowThrough)}</FieldValue>
          <FieldValue label="Amounts are estimates">{yesNo(container.isEstimate)}</FieldValue>
          <FieldValue label="SQG shipping 200+ miles">
            {yesNo(container.sqgOver200Miles)}
          </FieldValue>
        </Grid>
      </SectionCard>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <AccumulationClockSection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerFillCard container={container} />
        </Grid>
      </Grid>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 6 }}>
          <FillHistorySection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerHistorySection container={container} />
        </Grid>
      </Grid>

      {editDialogOpen && (
        <WasteContainerDialog container={container} onClose={() => setEditDialogOpen(false)} />
      )}
      <MoveContainerDialog
        container={container}
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
      />
      {statusDialogOpen && (
        <SetContainerStatusDialog
          container={container}
          onClose={() => setStatusDialogOpen(false)}
          onReconcileFollowUp={(choice) => {
            if (choice === ReconcileChoice.AddEvent) {
              setReconcileFillDialogOpen(true);
            } else if (choice === ReconcileChoice.AdjustCapacity) {
              setEditDialogOpen(true);
            }
          }}
        />
      )}
      {emptyDialogOpen && (
        <EmptyContainerDialog container={container} onClose={() => setEmptyDialogOpen(false)} />
      )}
      {consolidateDialogOpen && (
        <ConsolidateContainersDialog
          container={container}
          onClose={() => setConsolidateDialogOpen(false)}
        />
      )}
      {reconcileFillDialogOpen && (
        <ContainerFillEventDialog
          container={container}
          onClose={() => setReconcileFillDialogOpen(false)}
        />
      )}
    </Stack>
  );
}

export function WasteContainerDetail() {
  const { containerId } = useParams<{ containerId: string }>();
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery(GET_WASTE_CONTAINER_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId,
  });
  const container = data?.wasteContainer;

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Containers",
      to: tenantId ? `/o/${tenantId}/waste/containers` : undefined,
    },
    {
      label: container?.containerLabel ?? "Container",
    },
  ]);

  if (loading && !container) {
    return (
      <Stack spacing={2} sx={{ px: 3, py: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={48} />
        <Skeleton variant="rectangular" width="100%" height={280} />
        <Skeleton variant="rectangular" width="100%" height={120} />
      </Stack>
    );
  }

  if (error || !container) {
    return (
      <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
        <Typography variant="h6">Container not found</Typography>
        <Typography variant="body2" color="text.secondary">
          The container may have been archived, or the link may be incorrect.
        </Typography>
      </Stack>
    );
  }

  return <ContainerDetailContent container={container} />;
}
